import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { MapPin, Phone, Mail, Clock, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { getAttribution } from "@/lib/attribution";

export default function Contact() {
  const { t, lang, alternateUrls, localePath } = useI18n();
  const navigate = useNavigate();
  useSEO({
    title: t("contact.title"),
    description: lang === "en"
      ? "Contact L'Entracte, restaurant in Ville d'Avray: +33 1 82 00 18 67, restaurantlentractevda@gmail.com. Address, hours, directions."
      : "Contactez L'Entracte, restaurant à Ville d'Avray : 01 82 00 18 67, restaurantlentractevda@gmail.com. Adresse, horaires, plan d'accès.",
    lang,
    alternates: alternateUrls(),
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, error: "", success: false });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, error: "", success: false });
    try {
      const attribution = getAttribution() || {};
      await api.post("/contact/messages", { ...form, attribution });
      // Redirect to a dedicated thank-you page which fires the Meta Lead
      // and GA4 generate_lead conversion events.
      const q = new URLSearchParams({ form: "contact", subject: form.subject || "" }).toString();
      navigate(`${localePath("/merci")}?${q}`);
    } catch (err) {
      setStatus({ loading: false, error: formatApiErrorDetail(err.response?.data?.detail) || err.message, success: false });
    }
  };

  return (
    <div data-testid="contact-page" className="px-6 md:px-12 lg:px-16">
      <section className="py-20 md:py-28 max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-6">
          <p className="overline">{t("contact.overline")}</p>
          <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">{t("contact.title")}</h1>
          <p className="mt-6 text-[#697585] text-base md:text-lg max-w-xl">
            {t("contact.sub")}
          </p>

          <div className="mt-12 space-y-6 max-w-md">
            <div className="flex items-start gap-4">
              <MapPin size={20} className="text-[#B8956A] mt-1" />
              <div>
                <p className="overline">{t("contact.address")}</p>
                <p className="mt-1 text-[#203240]">3 rue de Versailles<br/>92410 Ville d'Avray</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone size={20} className="text-[#B8956A] mt-1" />
              <div>
                <p className="overline">{t("contact.phone")}</p>
                <a href="tel:+33182001867" className="mt-1 text-[#203240] block hover:text-[#B8956A]">01 82 00 18 67</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail size={20} className="text-[#B8956A] mt-1" />
              <div>
                <p className="overline">{t("contact.email")}</p>
                <p className="mt-1 text-[#203240]">restaurantlentractevda@gmail.com</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock size={20} className="text-[#B8956A] mt-1" />
              <div>
                <p className="overline">{t("contact.hours")}</p>
                <p className="mt-1 text-[#203240]">{t("contact.hoursValue")}<br/><span className="text-[#697585] text-sm">{t("contact.hoursSunday")}</span></p>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-6">
          <div className="border border-[#D9D3C5] bg-[#FFFFFF] p-8 md:p-10">
            <p className="overline">{t("contact.formOverline")}</p>
            <h2 className="font-display text-3xl mt-3">{t("contact.formTitle")}</h2>

            {status.success ? (
              <div data-testid="contact-success" className="mt-8 border border-[#B8956A]/50 bg-[#B8956A]/5 p-6">
                <p className="font-display text-2xl text-[#B8956A]">{t("contact.success")}</p>
                <p className="mt-2 text-[#697585]">{t("contact.successBody")}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="contact-form">
                <input data-testid="contact-input-name" required value={form.name} onChange={onChange("name")} placeholder={t("contact.fields.name")} className="field" />
                <input data-testid="contact-input-email" required type="email" value={form.email} onChange={onChange("email")} placeholder={t("contact.fields.email")} className="field" />
                <input data-testid="contact-input-phone" value={form.phone} onChange={onChange("phone")} placeholder={t("contact.fields.phone")} className="field" />
                <input data-testid="contact-input-subject" value={form.subject} onChange={onChange("subject")} placeholder={t("contact.fields.subject")} className="field" />
                <textarea data-testid="contact-input-message" required value={form.message} onChange={onChange("message")} placeholder={t("contact.fields.message")} className="field" rows={5} />
                {status.error && <p data-testid="contact-error" className="text-sm text-red-400">{status.error}</p>}
                <button data-testid="contact-submit" type="submit" disabled={status.loading} className="btn-primary w-full justify-center">
                  {status.loading ? t("contact.sending") : t("contact.submit")} <ArrowRight size={14} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto pb-28">
        <div className="aspect-[16/7] border border-[#D9D3C5] overflow-hidden">
          <iframe
            data-testid="contact-map"
            title="Carte L'Entracte - 3 rue de Versailles"
            src="https://www.openstreetmap.org/export/embed.html?bbox=2.180%2C48.823%2C2.197%2C48.830&layer=mapnik&marker=48.8263450%2C2.1882610"
            className="w-full h-full"
            style={{ border: 0 }}
          />
        </div>
        <a
          href="https://www.google.com/maps/place/3+Rue+de+Versailles,+92410+Ville-d%27Avray"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-2 link-gold text-sm"
        >
          {t("contact.mapOpen")} →
        </a>
      </section>
    </div>
  );
}
